import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ActionFilter from "../../components/ActionFilter";
import { deleteBadge, listBadges, listBadgeTypes } from "../../http/badges";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/slices/common";
import { debounce, limitChar } from "../../utils/common";
import AddBadgeModal from "./AddBadgeModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import CommonPagination from "../../components/Table/CommonPagination";
import ExpandableText from "../../components/ExpandableText";
import StatusFilter from "../../components/StatusFilter";
import ViewBadgeModal from "./ViewBadgeModal";
import PageLayout from "../../components/PageLayout";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  dialogContent: {
    maxWidth: "100%",
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: "calc(100vh - 195px)",
    maxHeight: "calc(100vh - 195px)",
    boxShadow: "none !important",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  tableHead: {
    position: "sticky",
    top: 0,
    backgroundColor: "#f5f5f5",
    zIndex: 1,
  },
  button: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    borderColor: "#142C5C !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  input: {
    marginBottom: "0 !important",
    "& label.Mui-focused": {
      color: "#142C5C",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#142C5C",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& fieldset": {
        borderColor: "#142C5C",
      },
      "&:hover fieldset": {
        borderColor: "#142C5C",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#142C5C",
      },
    },
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: "10px",
  },
  tableCell: {
    fontSize: {
      xs: "14px !important",
      sm: "15px !important",
      md: "15px !important",
      lg: "16px !important",
      xl: "16px !important",
    },
    color: "#000 !important",
    fontWeight: "600 !important",
    padding: {
      xs: "10px 5px !important",
      sm: "15px 10px !important",
      md: "15px 10px !important",
      lg: "20px 15px !important",
      xl: "20px 15px !important",
    },
    backgroundColor: "#e0e0e0",
  },
  actionsCell: {
    textAlign: "right",
  },
  tableBody: {
    fontSize: {
      xs: "12px !important",
      sm: "13px !important",
      md: "14px !important",
      lg: "15px !important",
      xl: "15px !important",
    },
    color: "#333 !important",
    padding: {
      xs: "10px 5px !important",
      sm: "10px !important",
      md: "10px !important",
      lg: "10px 15px !important",
      xl: "10px 15px !important",
    },
  },
  table: {
    border: "1px solid rgba(224, 224, 224, 1)",
    minWidth: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
}));
const ManageBadge = () => {
  const classes = useStyles();
  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [badges, setBadges] = useState([]);
  const [badgeTypes, setBadgeTypes] = useState([]);
  const [totalBadges, setTotalBadges] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedBadge, setSelectedBadge] = useState({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [filter, setFilter] = useState(0);
  const [page, setPage] = useState(0);
  const rowsPerPage = 15;

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    debounce(listBadgesHandler(0), 500);
  }, [searchText]);

  useEffect(() => {
    listBadgesHandler(page);
  }, [page, filter]);

  useEffect(() => {
    listBadgeTypesHandler();
  }, []);

  const listBadgeTypesHandler = async () => {
    try {
      dispatch(setLoading(true));
      const res = await listBadgeTypes();
      setBadgeTypes(res?.data);
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const listBadgesHandler = async (pg) => {
    let pgPayload;
    if (pg === 0) {
      setPage(0);
      pgPayload = pg + 1;
    } else pgPayload = page + 1;
    try {
      let payload = {
        page: pgPayload,
        limit: rowsPerPage,
        search: searchText,
      };
      if (filter) payload = { ...payload, type: filter };
      dispatch(setLoading(true));
      const res = await listBadges(payload);

      setBadges(res?.data?.list);
      setTotalBadges(res?.data?.resultCount);
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onView = (badge) => {
    setSelectedBadge(badge);
    setOpenView(true);
  };
  const onEdit = (badge) => {
    setSelectedBadge(badge);
    setOpenEdit(true);
  };

  const deleteHandler = (badge) => {
    setOpenDeleteModal(true);
    setSelectedBadge(badge);
  };

  const confirmDeleteHandler = async () => {
    try {
      dispatch(setLoading(true));
      const payload = { id: selectedBadge?._id };
      await deleteBadge(payload);
      await listBadgesHandler();
      setOpenDeleteModal(false);
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const cancelDeleteHandler = () => {
    setOpenDeleteModal(false);
  };

  return (
    <PageLayout title="Badges">
      <Box
        sx={{
          padding: "15px 15px 0 15px",
          borderRadius: "6px",
          boxShadow:
            "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "15px",
            flexDirection: {
              xs: "column",
              sm: "row",
              md: "row",
              lg: "row",
              xl: "row",
            },
          }}
        >
          <Box
            sx={{
              marginRight: "15px",
              display: "flex",
              alignItems: "center",
              width: {
                xs: "100%",
                sm: "auto",
                md: "auto",
                lg: "auto",
                xl: "auto",
              },
              marginBottom: {
                xs: "10px",
                sm: 0,
                md: 0,
                lg: 0,
                xl: 0,
              },
              justifyContent: {
                xs: "space-between",
                sm: "flex-start",
                md: "flex-start",
                lg: "flex-start",
                xl: "flex-start",
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: {
                  xs: "12px",
                  sm: "14px",
                  md: "15px",
                  lg: "15px",
                  xl: "16px",
                },
                paddingRight: "10px",
              }}
            >
              Status Filter:
            </Typography>
            <StatusFilter
              isBadge={true}
              badgeTypes={badgeTypes}
              setPostFilter={setFilter}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <input
                placeholder="Search"
                onChange={(e) => setSearchText(e.target.value)}
                type="text"
                className="search-field-ui"
              />
            </Box>
          </Box>
        </Box>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.stickyHeader}>
                <TableCell
                  sx={{
                    fontSize: {
                      xs: "14px",
                      sm: "15px",
                      md: "15px",
                      lg: "16px",
                      xl: "16px",
                    },
                    color: "#000",
                    fontWeight: 600,
                    padding: {
                      xs: "10px 5px",
                      sm: "15px 10px",
                      md: "15px 10px",
                      lg: "20px 15px",
                      xl: "20px 15px",
                    },
                    backgroundColor: "#e0e0e0",
                  }}
                >
                  Badge Logo
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: {
                      xs: "14px",
                      sm: "15px",
                      md: "15px",
                      lg: "16px",
                      xl: "16px",
                    },
                    color: "#000",
                    fontWeight: 600,
                    padding: {
                      xs: "10px 5px",
                      sm: "15px 10px",
                      md: "15px 10px",
                      lg: "20px 15px",
                      xl: "20px 15px",
                    },
                    backgroundColor: "#e0e0e0",
                  }}
                >
                  Badge Title
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: {
                      xs: "14px",
                      sm: "15px",
                      md: "15px",
                      lg: "16px",
                      xl: "16px",
                    },
                    color: "#000",
                    fontWeight: 600,
                    padding: {
                      xs: "10px 5px",
                      sm: "15px 10px",
                      md: "15px 10px",
                      lg: "20px 15px",
                      xl: "20px 15px",
                    },
                    backgroundColor: "#e0e0e0",
                  }}
                >
                  Badge Category
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: {
                      xs: "14px",
                      sm: "15px",
                      md: "15px",
                      lg: "16px",
                      xl: "16px",
                    },
                    color: "#000",
                    fontWeight: 600,
                    padding: {
                      xs: "10px 5px",
                      sm: "15px 10px",
                      md: "15px 10px",
                      lg: "20px 15px",
                      xl: "20px 15px",
                    },
                    backgroundColor: "#e0e0e0",
                  }}
                >
                  Description
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: {
                      xs: "14px",
                      sm: "15px",
                      md: "15px",
                      lg: "16px",
                      xl: "16px",
                    },
                    color: "#000",
                    fontWeight: 600,
                    padding: {
                      xs: "10px 5px",
                      sm: "15px 10px",
                      md: "15px 10px",
                      lg: "20px 15px",
                      xl: "20px 15px",
                    },
                    backgroundColor: "#e0e0e0",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {badges?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      textAlign: "center",
                      height: "calc(100vh - 270px)",
                      fontSize: {
                        xs: "12px",
                        sm: "13px",
                        md: "14px",
                        lg: "15px",
                        xl: "15px",
                      },
                      color: "#333",
                      padding: {
                        xs: "10px 5px",
                        sm: "10px",
                        md: "10px",
                        lg: "10px 15px",
                        xl: "10px 15px",
                      },
                    }}
                  >
                    No badges found
                  </TableCell>
                </TableRow>
              ) : (
                badges.map((badge) => (
                  <TableRow key={badge?._id}>
                    <TableCell
                      sx={{
                        textAlign: "left",
                        fontSize: {
                          xs: "12px",
                          sm: "13px",
                          md: "14px",
                          lg: "15px",
                          xl: "15px",
                        },
                        color: "#333",
                        padding: {
                          xs: "10px 5px",
                          sm: "10px",
                          md: "10px",
                          lg: "10px 15px",
                          xl: "10px 15px",
                        },
                      }}
                    >
                      <img
                        src={badge?.logo}
                        alt="logo"
                        style={{
                          paddingLeft: "20px",
                          height: "50px",
                          width: "50px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: "12px",
                          sm: "13px",
                          md: "14px",
                          lg: "15px",
                          xl: "15px",
                        },
                        color: "#333",
                        padding: {
                          xs: "10px 5px",
                          sm: "10px",
                          md: "10px",
                          lg: "10px 15px",
                          xl: "10px 15px",
                        },
                      }}
                    >
                      {limitChar(badge?.title)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: "12px",
                          sm: "13px",
                          md: "14px",
                          lg: "15px",
                          xl: "15px",
                        },
                        color: "#333",
                        padding: {
                          xs: "10px 5px",
                          sm: "10px",
                          md: "10px",
                          lg: "10px 15px",
                          xl: "10px 15px",
                        },
                      }}
                    >
                      {limitChar(badge?.category)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: "12px",
                          sm: "13px",
                          md: "14px",
                          lg: "15px",
                          xl: "15px",
                        },
                        color: "#333",
                        padding: {
                          xs: "10px 5px",
                          sm: "10px",
                          md: "10px",
                          lg: "10px 15px",
                          xl: "10px 15px",
                        },
                      }}
                    >
                      <ExpandableText
                        text={badge?.shortDescription}
                        maxLength={15}
                        title="Badge description"
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        fontSize: {
                          xs: "12px",
                          sm: "13px",
                          md: "14px",
                          lg: "15px",
                          xl: "15px",
                        },
                        color: "#333",
                        padding: {
                          xs: "10px 5px",
                          sm: "10px",
                          md: "10px",
                          lg: "10px 15px",
                          xl: "10px 15px",
                        },
                      }}
                    >
                      <ActionFilter
                        index={badge}
                        onView={onView}
                        onEdit={onEdit}
                        onDelete={deleteHandler}
                        badge={true}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CommonPagination
          rowsPerPage={rowsPerPage}
          count={totalBadges}
          page={page}
          onPageChange={handlePageChange}
        />
      </Box>
      {openView && (
        <ViewBadgeModal
          openDialog={openView}
          handleCloseDialog={() => setOpenView(false)}
          classes={classes}
          selectedBadge={selectedBadge}
        />
      )}
      {openEdit && (
        <AddBadgeModal
          openDialog={openEdit}
          handleCloseDialog={() => setOpenEdit(false)}
          classes={classes}
          edit={true}
          selectedBadge={selectedBadge}
          getList={listBadgesHandler}
          setSelectedBadge={setSelectedBadge}
        />
      )}

      {openDeleteModal && (
        <ConfirmDeleteModal
          onAccept={confirmDeleteHandler}
          onReject={cancelDeleteHandler}
        />
      )}
    </PageLayout>
  );
};

export default ManageBadge;
